// --------------------------------------------------------------
// Created On: 2023-09-20
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import isoToLocalDate from "src/utilities/time/isoToLocalDate";
import getAssetDisplayName from "src/utilities/getAssetDisplayName";
import isValueBooleanString from "src/utilities/isValueBooleanString";
import OperationDetailsPanel from "./OperationDetailsPanel/OperationDetailsPanel";
import Card from "src/components/Card/Card";
import {
  MS_PER_MINUTE,
  MAX_MINUTES_FOR_ACCURATE_READING,
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
  DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS,
} from "src/constants/miscellaneous";
import { POWER_TO_PANEL_ATTRIBUTE, ENGINE_RUNNING_ATTRIBUTE, RPM_ATTRIBUTE } from "src/constants/attributes";
import { LOCK_REMOTE_CONTROL_PERMISSION } from "src/constants/permissions";
import userHasPermission from "src/utilities/userHasPermission";
import useRemoteOperation, { ListAttribute, GaugeAttribute, Status } from "src/hooks/useRemoteOperation";
import Error from "src/components/Error/Error";
import Warning from "src/components/Warning/Warning";
import Gauge from "src/components/Gauge/Gauge";
import RemoteOperationPanel from "src/components/RemoteOperationPanel/RemoteOperationPanel";
import OperationLogList from "./OperationLogList/OperationLogList";
import InfoBanner from "src/components/InfoBanner/InfoBanner";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import Spinner from "src/components/Spinner/Spinner";
import styles from "./AssetRemoteOperationsPage.module.scss";
import Success from "src/components/Success/Success";
import AssetStatusBannerContainer from "src/components/AssetStatusBannerContainer/AssetStatusBannerContainer";

// Page that is used for remotely operating assets. Some possible actions are remote start/stop and speed change.
export default function AssetRemoteOperationsPage(): Component {
  const [isSampling, setIsSampling] = useState<boolean>(false);
  const [bannerLoading, setBannerLoading] = useState<boolean>(false);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [lastConfigAttemptUtc, setLastConfigAttemptUtc] = useState<string | null>(null);
  const [lastUpdateFirmwareAttemptUtc, setLastUpdateFirmwareAttemptUtc] = useState<string | null>(null);
  const [firmwareStatus, setFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [previousFirmwareStatus, setPreviousFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [configurationStatus, setConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [previousConfigurationStatus, setPreviousConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [firmwareUpdateInitialized, setFirmwareUpdateInitialized] = useState<boolean>(false);
  const [showConfirmLock, setShowConfirmLock] = useState<boolean>(false);
  const [showConfirmUnlock, setShowConfirmUnlock] = useState<boolean>(false);
  const { assetId } = useParams();
  const {
    assetData,
    isLockedOut,
    attributeReadings,
    logs,
    messageMap,
    loading: operationLoading,
    failedToLoad,
    isSamplingLiveData,
    deviceIsReadyForCommand,
    deviceReadyCheckedUtc,
    userCanOperate,
    actions,
  } = useRemoteOperation(parseInt(assetId ?? "", 10) || 0);
  const loading = operationLoading || bannerLoading;
  const canLock = userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]);
  const supportsRemoteOperation =
    assetData !== null && DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(assetData.deviceType || "");
  const supportsRemoteDeviceCommands =
    assetData !== null && DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS.includes(assetData.deviceType || "");
  const staleAttributeCodes = useMemo(
    () => getStaleAttributeCodes([...attributeReadings.listAttributes, ...attributeReadings.gaugeAttributes]),
    [JSON.stringify(attributeReadings.listAttributes), JSON.stringify(attributeReadings.gaugeAttributes)]
  );

  // Keep the status banner's view of the asset in sync with each poll.
  // We want to avoid overwriting the previous configuration or firmware status if it is in progress so that success
  // and failure banners stay on the page if the configuration or firmware update is applied while the user is on the
  // page. If we do not do this, any success or failure banners that display to the user in response to applying
  // configuration or updating firmware will disappear the next time data is polled.
  useEffect(() => {
    if (assetData !== null) {
      setIsMigrating(assetData.isMigrating);
      setLastConfigAttemptUtc(assetData.lastConfigAttemptUtc);
      setLastUpdateFirmwareAttemptUtc(assetData.lastUpdateFirmwareAttemptUtc);
      if (previousConfigurationStatus !== "IN_PROGRESS") {
        setPreviousConfigurationStatus(configurationStatus);
      }
      if (assetData.configurationStatus !== null) {
        setConfigurationStatus(assetData.configurationStatus);
      }
      if (previousFirmwareStatus !== "IN_PROGRESS") {
        setPreviousFirmwareStatus(firmwareStatus);
      }
      if (assetData.firmwareStatus !== null) {
        setFirmwareStatus(assetData.firmwareStatus);
      }
    }
  }, [assetData]);

  // Updates the migration state based on if the asset has successfully completed migration or not.
  function handleMigrateAsset(status: Status, previousStatus: Status): void {
    // Consider the migration completed if a migration was in progress and is now successful.
    setIsMigrating(!(status === "SUCCEEDED" && previousStatus === "IN_PROGRESS"));
    void actions.refreshLogs();
  }

  // Updates the configuration status based on if migrating an asset or applying the configuration successfully started.
  function handleApplyConfiguration(status: Status): void {
    setLastConfigAttemptUtc(null);
    setPreviousConfigurationStatus(configurationStatus);
    setConfigurationStatus(status);
    void actions.refreshLogs();
  }

  // Updates the firmware status based on if updating a device's firmware successfully started.
  // When a firmware update is initialized, lock remote operation controls and update the status logs.
  function handleUpdateFirmware(status: Status): void {
    setLastUpdateFirmwareAttemptUtc(null);
    setFirmwareUpdateInitialized(true);
    setPreviousFirmwareStatus(firmwareStatus);
    setFirmwareStatus(status);
    void actions.refreshLogs();
  }

  // Ask the user to confirm before changing the lock status.
  function handleLockStatus(isLocked: boolean): void {
    if (isLocked) {
      setShowConfirmLock(true);
    } else {
      setShowConfirmUnlock(true);
    }
  }

  // Close the confirm dialogs and apply the lock change.
  function confirmLockStatus(isLocked: boolean): void {
    setShowConfirmLock(false);
    setShowConfirmUnlock(false);
    void actions.updateLockStatus(isLocked);
  }

  // Returns an array of attribute codes that represent attributes with stale data.
  function getStaleAttributeCodes(attributes: (ListAttribute | GaugeAttribute)[]): string[] {
    const staleAttributeCodes: string[] = [];
    const nowDate = new Date();

    // Mark attributes as stale if they haven't reported a new reading in a certain amount of time.
    attributes.forEach((attribute) => {
      if (attribute.currentValueUtc === null) {
        staleAttributeCodes.push(attribute.attributeCode);
      } else {
        const attributeDate = isoToLocalDate(attribute.currentValueUtc);
        const attributeIsStale =
          (nowDate.getTime() - attributeDate.getTime()) / MS_PER_MINUTE > MAX_MINUTES_FOR_ACCURATE_READING;
        if (attributeIsStale) {
          staleAttributeCodes.push(attribute.attributeCode);
        }
      }
    });

    // Some attributes values indicate that other attributes may not have a reliable value.
    // In these cases we will mark attributes as stale if they cannot be trusted.
    const powerToPanelAttribute = attributes.find((attribute) => attribute.attributeCode === POWER_TO_PANEL_ATTRIBUTE);

    const engineRunningAttribute = attributes.find((attribute) => attribute.attributeCode === ENGINE_RUNNING_ATTRIBUTE);

    if (
      !staleAttributeCodes.includes(ENGINE_RUNNING_ATTRIBUTE) &&
      powerToPanelAttribute !== undefined &&
      powerToPanelAttribute.currentValue !== null &&
      isValueBooleanString(powerToPanelAttribute.currentValue) &&
      powerToPanelAttribute.currentValue.toLowerCase() === "false"
    ) {
      staleAttributeCodes.push(ENGINE_RUNNING_ATTRIBUTE);
    }

    if (
      !staleAttributeCodes.includes(RPM_ATTRIBUTE) &&
      ((powerToPanelAttribute !== undefined &&
        powerToPanelAttribute.currentValue !== null &&
        isValueBooleanString(powerToPanelAttribute.currentValue) &&
        powerToPanelAttribute.currentValue.toLowerCase() === "false") ||
        (engineRunningAttribute !== undefined &&
          engineRunningAttribute.currentValue !== null &&
          isValueBooleanString(engineRunningAttribute.currentValue) &&
          engineRunningAttribute.currentValue.toLowerCase() === "false"))
    ) {
      staleAttributeCodes.push(RPM_ATTRIBUTE);
    }

    return staleAttributeCodes;
  }

  return (
    <div className="p-4 mx-auto">
      {attributeReadings.gaugeAttributes.length > 0 && (
        <div className="row align-items-center">
          {attributeReadings.gaugeAttributes.map((attribute, i) => (
            <div className={`col text-center ${styles.gaugeColumn}`} key={i}>
              <Gauge
                code={attribute.attributeCode}
                label={attribute.attributeName}
                min={attribute.minValue}
                max={attribute.maxValue}
                value={attribute.currentValue}
                time={attribute.currentValueUtc}
                unitShortName={attribute.unitShortName}
                staleAttributeCodes={staleAttributeCodes}
                loading={isSamplingLiveData}
              />
            </div>
          ))}
        </div>
      )}

      {messageMap.error.length === 0 &&
        !failedToLoad &&
        !userCanOperate &&
        !isMigrating &&
        !loading &&
        (assetData === null || !assetData.operationIsAllowed) && (
          <div className="my-4 px-3">
            <InfoBanner message="You do not have permission to remotely operate this asset." />
          </div>
        )}

      <div className="my-4 px-3">
        <AssetStatusBannerContainer
          loading={loading}
          assetId={assetId ?? ""}
          deviceId={assetData === null ? null : assetData.deviceId}
          firmwareStatus={firmwareStatus}
          configurationStatus={configurationStatus}
          previousFirmwareStatus={previousFirmwareStatus}
          previousConfigurationStatus={previousConfigurationStatus}
          deviceIdentifier={assetData === null ? "" : assetData.deviceIdentifier ?? ""}
          deviceTypeName={assetData === null ? "" : assetData.deviceType ?? ""}
          isMigrating={isMigrating}
          isRented={assetData !== null && assetData.isRented}
          onLatestFirmware={assetData === null ? null : assetData.onLatestFirmware}
          lastConfigAttemptUtc={lastConfigAttemptUtc}
          lastUpdateFirmwareAttemptUtc={lastUpdateFirmwareAttemptUtc}
          lastComm={assetData === null ? null : assetData.lastComm}
          hideLastComm={true}
          onChangeLoading={(loading) => setBannerLoading(loading)}
          onMigrateAsset={(status, previousStatus) => handleMigrateAsset(status, previousStatus)}
          onApplyConfiguration={(status) => handleApplyConfiguration(status)}
          onUpdateFirmware={(status) => handleUpdateFirmware(status)}
        />
      </div>

      {messageMap.error.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Error message={messageMap.error} />
        </div>
      )}

      {failedToLoad && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Error message="Unable to load remote operation data for this asset. Please try again." />
        </div>
      )}

      {!failedToLoad && assetData !== null && (assetData.deviceId === null || !supportsRemoteOperation) && !canLock && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Error message="This asset does not support remote control operation due to its monitoring device / controller configuration." />
        </div>
      )}
      {messageMap.warning.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Warning message={messageMap.warning} />
        </div>
      )}

      {messageMap.success.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Success message={messageMap.success} />
        </div>
      )}

      <div className="row justify-content-center mt-2 mb-4 gx-0 mx-0">
        {(attributeReadings.listAttributes.length > 0 || attributeReadings.digitalOutputs.length > 0) && (
          <div
            className={
              userCanOperate || attributeReadings.listAttributes.length > 0
                ? "col-12 col-lg-6 col-xl-8 mb-4 mb-lg-0"
                : "col-12"
            }
          >
            <OperationDetailsPanel
              assetId={assetId === undefined ? 0 : parseInt(assetId, 10)}
              attributes={attributeReadings.listAttributes}
              staleAttributeCodes={staleAttributeCodes}
              digitalOutputs={attributeReadings.digitalOutputs}
              deviceId={assetData === null ? null : assetData.deviceId}
              deviceType={assetData === null ? null : assetData.deviceType}
              userCanOperate={userCanOperate}
              deviceIsReadyForCommand={deviceIsReadyForCommand}
              deviceReadyCheckedUtc={deviceReadyCheckedUtc}
              loading={loading || isMigrating}
              onIsSamplingChange={(isSampling) => setIsSampling(isSampling)}
            />
          </div>
        )}

        {/* We show remote operation controls only if the asset is not locked out and has a valid device. */}
        {assetData !== null &&
          !isLockedOut &&
          assetData.operationIsAllowed &&
          (supportsRemoteOperation || supportsRemoteDeviceCommands || canLock) && (
            <div className="col-12 col-lg-6 col-xl-4 mb-4 mb-lg-0">
              <RemoteOperationPanel
                assetName={getAssetDisplayName(assetData.assetName, assetData.assetNickname)}
                deviceId={assetData.deviceId}
                deviceType={assetData.deviceType}
                isDeviceAttached={assetData.isDeviceAttached}
                isMigrating={isMigrating}
                remoteOperationSupported={assetData.remoteOperationSupported}
                controllerApplicationType={assetData.controllerApplicationType}
                controllerModel={assetData.controllerModel}
                isLockedOut={isLockedOut}
                canUnlock={assetData.canUnlock}
                loading={loading || isSampling}
                firmwareUpdateInitialized={firmwareUpdateInitialized}
                onLatestFirmware={assetData.onLatestFirmware}
                digitalOutputs={attributeReadings.digitalOutputs}
                onChangeCommunicationMode={(command) => actions.changeCommunicationMode(command)}
                onChangeSpeed={(command) => actions.changeSpeed(command)}
                onChangeRunningStatus={(command) => actions.changeRunningStatus(command)}
                onClearFailedCommands={() => actions.clearFailedCommands()}
                onRestartMonitoringDevice={() => actions.restartMonitoringDevice()}
                onReapplyConfiguration={() => actions.reapplyConfiguration()}
                onBlinkLeds={(pattern, position) => actions.blinkLeds(pattern, position)}
                onError={(error) => actions.setErrorMessage(error)}
                onChangeLock={(isLocked) => handleLockStatus(isLocked)}
                onChangeValvePosition={(valveNumber, percentOpen) =>
                  actions.changeValvePosition(valveNumber, percentOpen)
                }
                onChangeDigitalOutput={(position, state) => actions.changeDigitalOutput(position, state)}
              />
            </div>
          )}

        {/* We show this message if remote operation is locked for this asset. */}
        {assetData !== null && !loading && isLockedOut && (
          <div
            className={attributeReadings.listAttributes.length > 0 ? "col-12 col-lg-6 col-xl-4 mb-4 mb-lg-0" : "col-12"}
          >
            <Card title={`${getAssetDisplayName(assetData.assetName, assetData.assetNickname)} Remote Operations`}>
              <p className="h4 text-center py-5 px-4">
                Remote control access is locked for this asset.
                {assetData.canUnlock
                  ? ""
                  : " The lock was applied by the company you are renting this asset from, only they can unlock this asset."}
              </p>
              {assetData.canUnlock && canLock && (
                <div className="text-center">
                  <button
                    className="btn btn-primary mx-auto mb-5"
                    type="button"
                    disabled={isMigrating}
                    onClick={() => handleLockStatus(false)}
                  >
                    Unlock Remote Control
                  </button>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {assetData !== null && logs.length > 0 && (
        <OperationLogList
          assetName={getAssetDisplayName(assetData.assetName, assetData.assetNickname)}
          operationLogs={logs}
        />
      )}

      <ConfirmModal
        showModal={showConfirmLock}
        title="Apply Remote Lock?"
        content={
          "Are you sure that you want to apply a remote lock on this asset? " +
          "This will prevent all remote operations for this asset until the lock is removed."
        }
        yesText="Apply Remote Lock"
        noText="Cancel"
        danger={false}
        onClose={() => setShowConfirmLock(false)}
        onYes={() => confirmLockStatus(true)}
        onNo={() => setShowConfirmLock(false)}
      />

      <ConfirmModal
        showModal={showConfirmUnlock}
        title="Remove Remote Lock?"
        content="Are you sure that you want to remove the remote lock on this asset?"
        yesText="Remove Remote Lock"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmUnlock(false)}
        onYes={() => confirmLockStatus(false)}
        onNo={() => setShowConfirmUnlock(false)}
      />

      <Spinner loading={loading} />
    </div>
  );
}
